import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Button from '../components/button/Button';
import Icon from '../components/icon/Icon';
import Table from '../components/table/table';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import Image from '../components/image/Image';
import { ITableData } from '../types/interfaces';
import { routes } from '../routes/routePaths';
import NewFundModal, { FundToEdit } from '../components/modals/NewFundModal';
import { getFunds } from '../services/GuaranteesService';

interface Fund {
  id: number;
  name: string;
  is_tax_deductible: boolean;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface FundTableRow {
  id: number;
  name: string;
  is_tax_deductible: boolean;
}

const Funds = () => {
  const navigate = useNavigate();
  const [funds, setFunds] = useState<FundTableRow[]>([]);
  const [tableData, setTableData] = useState<ITableData>();
  const [page, setPage] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isNewFundModalOpen, setIsNewFundModalOpen] = useState<boolean>(false);
  const [editingFund, setEditingFund] = useState<FundToEdit | null>(null);

  useEffect(() => {
    fetchFunds();
  }, [page]);

  const fetchFunds = async () => {
    setIsLoading(true);
    try {
      const response = await getFunds();
      if (response.success && 'data' in response) {
        const fundsData: Fund[] = response.data;

        const mappedFunds: FundTableRow[] = fundsData.map((fund) => ({
          id: fund.id,
          name: fund.name,
          is_tax_deductible: fund.is_tax_deductible,
        }));

        setFunds(mappedFunds);

        setTableData({
          size: mappedFunds.length,
          page: 0,
          totalElements: mappedFunds.length,
          totalPages: 1,
        });
      } else {
        setFunds([]);
      }
    } catch (error) {
      console.error('Error fetching funds:', error);
      setFunds([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (data: number) => {
    if (data < 0 || data === tableData?.totalPages) {
      return;
    }
    setPage(data);
  };

  const handleGoBack = () => {
    navigate(routes.settings);
  };

  const handleOpenNewFundModal = () => {
    setEditingFund(null);
    setIsNewFundModalOpen(true);
  };

  const handleEditFund = (row: FundTableRow) => {
    setEditingFund({
      id: row.id,
      name: row.name,
      is_tax_deductible: row.is_tax_deductible,
    });
    setIsNewFundModalOpen(true);
  };

  const handleCloseNewFundModal = () => {
    setIsNewFundModalOpen(false);
    setEditingFund(null);
  };

  const handleFundSaved = () => {
    fetchFunds();
  };

  const columns = [
    {
      name: 'Nombre',
      selector: (row: FundTableRow) => row.name,
      sortable: false,
      minWidth: '300px',
    },
    {
      name: 'Es desgravable',
      selector: (row: FundTableRow) => (row.is_tax_deductible ? 'SI' : 'NO'),
      sortable: false,
      minWidth: '200px',
    },
    {
      name: '',
      cell: (row: FundTableRow) => (
        <Card>
          <Icon
            name='edit_outline'
            width={20}
            height={20}
            cursor='pointer'
            variant='info'
            onClick={() => handleEditFund(row)}
            aria-label={`Editar fondo ${row.name}`}
          />
        </Card>
      ),
      center: true,
      width: '56px',
    },
  ];

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing={'4'}
      height='-webkit-fill-available'
      width='85vw'
    >
      <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ bottom: '4' }}>
        <Card display='flex' alignItems='center' gap='16px'>
          <Button
            icon='arrow_left_outline'
            variant='tertiary'
            size='sm'
            iconAlign='center'
            onClick={handleGoBack}
            aria-label="Volver a configuración"
          />
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Fondos
          </H>
        </Card>
        <Button variant='primary' onClick={handleOpenNewFundModal}>
          Nuevo fondo
        </Button>
      </Card>

      {isLoading ? (
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={10} columns={columns.length} />
        </Card>
      ) : funds.length > 0 ? (
        <Card margin={{ top: '4' }} justifyContent='center' alignItems='center'>
          <Table
            columns={columns}
            data={funds}
            tableData={tableData}
            handleChangePage={(e: any) => handleChangePage(e)}
          />
        </Card>
      ) : (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No hay fondos creados.
          </P>
          <P variant='primary' align='center'>
            Podés crear uno nuevo.
          </P>
          <Button variant='primary' onClick={handleOpenNewFundModal}>
            Nuevo fondo
          </Button>
        </Card>
      )}

      <NewFundModal
        isOpen={isNewFundModalOpen}
        onClose={handleCloseNewFundModal}
        onSuccess={handleFundSaved}
        fundToEdit={editingFund}
      />
    </Card>
  );
};

export default Funds;
